import React from 'react'
import clsx from 'clsx'
import { MdDragHandle } from 'react-icons/md'
import { Trash } from 'iconsax-react'
import { TextareaAutosize, useTheme } from '@mui/material'
import { useDrag, useDrop } from 'react-dnd'
import { useAppDispatch } from '@SonarCX/hooks/redux'

import GroupField from '../GroupField'

import { ITEM_TYPE } from '../../../CustomField'
import { cleanGroup } from './helpers'
import { arraysEqual, moveItem } from '@SonarCX/utilities'
import {
  deleteCustomFieldGroup,
  updateCustomFieldGroup
} from '@SonarCX/slices/customFields/groups'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

type GroupProps = {
  group: APICustomFieldGroup
  isFirst: boolean
  isLast: boolean
  onMove: (source: string, target: string, above: boolean) => void
}

const Group: React.FC<GroupProps> = ({ group, isFirst, isLast, onMove }) => {
  const theme = useTheme()

  const dispatch = useAppDispatch()

  const { setTitle, setAction, setSubtext, setV2 } = useAreYouSure()

  const handleDropAdd = React.useCallback(
    (item: any) => {
      if (group.field_ids.includes(item.id)) {
        return
      }

      const newGroup = cleanGroup(group)
      newGroup.field_ids = [...newGroup.field_ids, item.id]
      dispatch(updateCustomFieldGroup(newGroup))
    },
    [group, dispatch]
  )

  const [{ isOver }, addDropRef] = useDrop({
    accept: ITEM_TYPE,
    drop: handleDropAdd,
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [, dragRef, dragPreviewRef] = useDrag({
    item: { type: 'custom-field-group', id: group.id }
  })

  const [{ isOver: isOverTop }, dropTopRef] = useDrop({
    accept: 'custom-field-group',
    drop: (item: any) => onMove(item.id, group.id, true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottomRef] = useDrop({
    accept: 'custom-field-group',
    drop: (item: any) => onMove(item.id, group.id, false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const classes = useStyles({ isOver })

  const handleBlurSave = React.useCallback(
    (prop: string, val: string) => {
      const original = group[prop]
      const newVal = val.trim()

      if ((prop === 'title' && newVal.length === 0) || newVal === original) {
        return
      }

      const newGroup = cleanGroup(group)
      if (prop === 'description') {
        newGroup.description = newVal === '' ? null : newVal
      } else {
        newGroup[prop] = newVal
      }
      dispatch(updateCustomFieldGroup(newGroup))
    },
    [group, dispatch]
  )

  const handleDeleteField = React.useCallback(
    (fieldID: string) => {
      if (!group.field_ids.includes(fieldID)) {
        return
      }

      const newGroup = cleanGroup(group)
      newGroup.field_ids = newGroup.field_ids.filter(
        itemID => itemID !== fieldID
      )
      dispatch(updateCustomFieldGroup(newGroup))
    },
    [group, dispatch]
  )

  const handleGroupDelete = React.useCallback(() => {
    setTitle(`Delete "${group.title}"`)
    setSubtext('Are you sure you want to delete this group?')
    setV2(true)
    setAction(() => {
      dispatch(deleteCustomFieldGroup(group.id))
    })
  }, [setTitle, setSubtext, setV2, setAction, group, dispatch])

  const handleFieldMove = React.useCallback(
    (source: string, target: string, above: boolean) => {
      const moved = moveItem(group.field_ids, source, target, above)

      if (arraysEqual(group.field_ids, moved, true)) {
        return
      }

      const newGroup = cleanGroup(group)
      newGroup.field_ids = moved
      dispatch(updateCustomFieldGroup(newGroup))
    },
    [group, dispatch]
  )

  return (
    <>
      <div
        ref={dropTopRef}
        className={clsx(
          classes.dropZone,
          isOverTop && (isFirst ? classes.dropZoneTop : classes.dropZoneMiddle)
        )}
      />

      <div
        ref={dragPreviewRef}
        className={clsx(classes.root, isLast && classes.last)}
      >
        <div className={classes.title}>
          <div ref={dragRef} className={classes.dragHandle}>
            <MdDragHandle className={classes.dragIcon} />
          </div>

          <input
            placeholder='Group title'
            defaultValue={group.title}
            onBlur={e => handleBlurSave('title', e.target.value)}
            className={classes.name}
          />

          <Trash
            onClick={handleGroupDelete}
            size={theme.size.base}
            className={classes.closeIcon}
          />
        </div>

        <div className={classes.descriptionArea}>
          <div className={classes.label}>Description</div>

          <TextareaAutosize
            minRows={1}
            maxRows={5}
            placeholder='Group description'
            defaultValue={group.description}
            onBlur={e => handleBlurSave('description', e.target.value)}
            className={classes.description}
          />
        </div>

        {group.field_ids.length === 0 ? (
          <div
            ref={addDropRef}
            className={clsx(classes.fieldsDropbox, classes.noFields)}
          >
            Drag fields here to add
          </div>
        ) : (
          <>
            <div className={classes.label}>Fields</div>

            <div className={classes.fields}>
              {group.field_ids.map((id, i) => (
                <GroupField
                  key={id}
                  id={id}
                  isFirst={i === 0}
                  isLast={i === group.field_ids.length - 1}
                  onDelete={() => handleDeleteField(id)}
                  groupID={group.id}
                  onMove={handleFieldMove}
                />
              ))}

              <div
                ref={addDropRef}
                className={clsx(classes.fieldsDropbox, classes.addFields)}
              >
                Drag fields here to add
              </div>
            </div>
          </>
        )}
      </div>

      {isLast && (
        <div
          ref={dropBottomRef}
          className={clsx(
            classes.dropZone,
            isOverBottom && classes.dropZoneBottom
          )}
        />
      )}
    </>
  )
}

export default Group
